import { numberToDecimalString as toDec } from 'commons/utils/number'
import { type HeadItemType, Table, Text } from 'components'
import { PaginationV2 } from 'components/Table/PaginationV2'
import { TableWrapper } from 'container/FairShare/styles'
import { t } from 'i18next'
import { useFormContext } from 'react-hook-form'
import type { FairShareOfficeData } from 'services/fairShare/interfaces'
import {
  fetchOfficeMaterialColors,
  setPage,
  setPageSize,
  setSharesOfficeData,
} from 'store/FairShareContext/fairShareSlice'
import { useDispatch, useSelector } from 'store/store'
import { theme } from 'styles/theme/hav-theme'

export const OfficeTable = () => {
  const dispatch = useDispatch()
  const { getValues } = useFormContext()
  const {
    sharesOffice: { data, totals },
    loadingTableItem,
    pagination: { currentPage, pageSize },
  } = useSelector((state) => state.fairShare)

  const tableLabels: HeadItemType[] = [
    { columnName: 'material', label: t('common.material') },
    { columnName: 'code', label: t('common.code') },
    { columnName: 'center', label: t('common.center') },
    { columnName: 'target', label: t('common.target') },
    { columnName: 'approved', label: t('common.approved') },
    { columnName: 'typed', label: t('common.typed') },
    { columnName: 'share', label: t('screens.fairShare.share') },
    { columnName: 'availableBalance', label: t('screens.fairShare.availableBalance') },
    { columnName: 'tolerance', label: t('screens.fairShare.tolerance') },
    { columnName: 'level', label: t('common.level') },
  ]

  const tableData = () => {
    return data.map((item) => {
      const id = `${item.code}-${item.center}-${item.description}`
      return {
        checked: item.checked ?? false,
        content: [
          item.description,
          item.code,
          item.center,
          toDec(item.target),
          toDec(item.approved),
          <Text
            customColor={theme.colors.ion.success.strong}
            key={`typed-${id}`}
            size="xs"
            weight="lg"
          >{`${toDec(item.typed)}`}</Text>,
          <Text
            customColor={theme.colors.ion.success.strong}
            key={`share-${id}`}
            size="xs"
            weight="lg"
          >{`${toDec(item.share)} %`}</Text>,
          toDec(item.availableBalance),
          '-',
          item.selectedLevel,
        ],
        expanded: item.expanded ?? false,
        id,
        subRows: tableSubrows(item),
      }
    })
  }

  const tableSubrows = (item: FairShareOfficeData) => {
    return item.colors?.map((color) => {
      const id = `${color.code}-${color.description}`
      return {
        content: [
          color.description,
          color.code,
          '',
          toDec(color.target),
          toDec(color.approved),
          <Text
            customColor={theme.colors.ion.success.strong}
            key={`typed-${id}`}
            size="xs"
            weight="lg"
          >{`${toDec(color.typed)}`}</Text>,
          <Text
            customColor={theme.colors.ion.success.strong}
            key={`share-${id}`}
            size="xs"
            weight="lg"
          >{`${toDec(color.share)} %`}</Text>,
          toDec(color.availableBalance),
          `${toDec(color.extrapolationPercentage)} %`,
          '',
        ],
        id,
      }
    })
  }

  const tableSummary = () => {
    return [
      t('common.totalItems'),
      '',
      '',
      toDec(totals?.totalTargetQuantity),
      toDec(totals?.totalApprovedQuantity),
      toDec(totals?.totalTypedQuantity),
      `${toDec(totals?.share)} %`,
      toDec(totals?.availableBalance),
      '',
      '',
    ]
  }

  const setExpanded = (id: string) => {
    const [code, center] = id.split('-')
    const selectedItem = data.find((item) => item.code === code && item.center === center)
    const isItemExpanded = selectedItem?.expanded
    const hasLoadedColors = selectedItem?.colors?.length

    if (isItemExpanded || hasLoadedColors) {
      const newMaterialList = data.map((item) => {
        if (item.code === code && item.center === center) {
          return { ...item, expanded: !item?.expanded }
        }
        return item
      })
      dispatch(setSharesOfficeData(newMaterialList))
    } else {
      dispatch(
        fetchOfficeMaterialColors({
          businessUnit: getValues('businessUnit'),
          centerCode: center,
          materialCode: code,
          officeCode: getValues('office'),
          regionCode: getValues('zone'),
          teamCode: getValues('regional'),
          yearMonth: Number(getValues('year') + getValues('month')),
        }),
      )
    }
  }

  const setChecked = (checked: boolean, id?: string) => {
    if (!id) {
      const newMaterialList = data.map((item) => {
        return { ...item, checked }
      })
      dispatch(setSharesOfficeData(newMaterialList))
      return
    }

    const [code, center] = id.split('-')
    const newMaterialList = data.map((item) => {
      if (item.code === code && item.center === center) {
        return { ...item, checked: !item.checked }
      }
      return item
    })
    dispatch(setSharesOfficeData(newMaterialList))
  }

  return (
    <TableWrapper>
      <Table
        bodyData={tableData()}
        checkboxSize="small"
        distributeWidth={true}
        hasAsyncExpand
        hasCheckbox
        headData={tableLabels}
        isLoading={loadingTableItem}
        rowHeight={48}
        setChecked={setChecked}
        summaryRow={tableSummary()}
        toggleExpand={setExpanded}
      />
      <PaginationV2
        currentPage={currentPage}
        itemsPerPage={pageSize}
        itemsPerPageOptions={[20, 40, 60, 80, 100]}
        onItemsPerPageChange={(size) => dispatch(setPageSize(size))}
        onPageChange={(page) => dispatch(setPage(page))}
        totalItems={totals?.rowsCount || 0}
      />
    </TableWrapper>
  )
}
